import { InputBase, InputLabel } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import amex from 'payment-icons/min/flat/amex.svg';
import mastercard from 'payment-icons/min/flat/mastercard.svg';
import visa from 'payment-icons/min/flat/visa.svg';
import unknown from 'payment-icons/min/outline/default.svg';
import * as React from 'react';
import { useContext, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { FormContext } from 'src/components/form/Form';
import { getError } from 'src/components/form/utils/getError';
import { transformStringInput } from 'src/components/form/utils/transformStringInput';
import { transformStringOutput } from 'src/components/form/utils/transformStringOutput';
import { CardBrands } from 'src/constants/CardBrand';
import { translate } from 'src/i18n/translate';
import { isBlankString } from 'src/utils/string/isBlankString';

export function FormCardNumberField({
    name,
    label,
    placeholder,
    helperText,
    value,
    onChange,
    variant = 'outlined',
    required,
    disabled,
    InputProps,
    rules,
    defaultValue = null,
}: Props): React.ReactElement {
    const classes = useStyles();
    const theme = useTheme();

    const {
        errors,
        control,
        formState: { isSubmitting },
        watch,
    } = useFormContext();
    const formContext = useContext(FormContext);
    const inputRef = useRef<HTMLInputElement>();
    const brand = watch(`${name}.brand`);
    const expiry = watch(`${name}.expiry`);
    const cvc = watch(`${name}.cvc`);

    const minLength = 13;
    const maxLength = 19;

    return (
        <Controller
            render={({ value, onChange, ...props }) => (
                <div>
                    <InputLabel classes={{ root: classes.label, error: classes.labelError }} error={!!getError(errors, name)} required={required}>
                        {label}
                    </InputLabel>
                    <InputBase
                        {...props}
                        value={transformStringInput(value)}
                        onChange={(value) => onChange(transformStringOutput(value as any))}
                        /* @ts-ignore */
                        label={label}
                        placeholder={'1234 1234 1234 1234'}
                        // helperText={getError(errors, name)?.['cardNumber']?.message ?? helperText}
                        error={!!getError(errors, name)?.['cardNumber'] || !!getError(errors, name)?.['expiry'] || !!getError(errors, name)?.['cvc']?.message}
                        inputRef={inputRef}
                        inputComponent={NumberFormatCustom as any}
                        startAdornment={
                            !!brand && (
                                <InputAdornment position='start'>
                                    <span>**** **** ****</span>
                                </InputAdornment>
                            )
                        }
                        endAdornment={
                            <InputAdornment position='end'>
                                {brand === CardBrands.VISA && <img src={visa} alt='' height={theme.spacing(2.5)} />}
                                {brand === CardBrands.MASTERCARD && <img src={mastercard} alt='' height={theme.spacing(2.5)} />}
                                {brand === CardBrands.AMERICAN_EXPRESS && <img src={amex} alt='' height={theme.spacing(2.5)} />}
                                {!isBlankString(brand) && brand !== CardBrands.VISA && brand !== CardBrands.MASTERCARD && brand !== CardBrands.AMERICAN_EXPRESS && (
                                    <img src={unknown} alt='' height={theme.spacing(2.5)} style={{ opacity: 0.7 }} />
                                )}
                                {isBlankString(brand) && (
                                    <>
                                        <img src={visa} alt='' height={theme.spacing(2.5)} />
                                        <Box p={0.25} />
                                        <img src={mastercard} alt='' height={theme.spacing(2.5)} />
                                        {/*<Box p={0.25} />*/}
                                        {/*<img src={amex} alt="" height={theme.spacing(2.5)} />*/}
                                    </>
                                )}
                            </InputAdornment>
                        }
                        autoComplete={'cc-number'}
                        variant={variant}
                        required={required}
                        disabled={isSubmitting || disabled || formContext.disabled}
                        fullWidth
                        classes={{ root: classes.input, error: classes.inputError }}
                    />
                    {!!helperText && <FormHelperText classes={{ root: !!getError(errors, name) ? classes.helperTextError : classes.helperText }}>{helperText}</FormHelperText>}
                </div>
            )}
            control={control}
            name={`${name}.cardNumber`}
            defaultValue={defaultValue}
            rules={
                disabled
                    ? undefined
                    : {
                          required: {
                              value: rules?.required || !isBlankString(expiry) || !isBlankString(cvc),
                              message: translate('Card number is required'),
                          },
                          minLength: {
                              value: minLength,
                              message: translate('Invalid card number'),
                          },
                          maxLength: {
                              value: maxLength,
                              message: translate('Invalid card number'),
                          },
                      }
            }
            onFocus={() => {
                // make focus on error work when disabled={isSubmitting || disabled}
                if (inputRef.current) {
                    inputRef.current.disabled = false;
                    inputRef.current.focus();
                }
            }}
        />
    );
}

export function NumberFormatCustom(props: { inputRef: any; name: string; onChange: any }): React.ReactElement {
    const { inputRef, onChange, ...other } = props;

    return <NumberFormat {...other} getInputRef={inputRef} onValueChange={(values) => onChange(values.value)} thousandSeparator isNumericString format='#### #### #### #### ####' decimalScale={0} />;
}

const useStyles = makeStyles((theme) => ({
    input: {
        backgroundColor: theme.palette.secondary.light,
        borderRadius: 5,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: 5,
        paddingBottom: 5,
        fontFamily: theme.typography.regular,
    },
    inputError: {
        border: '2px solid red',
    },
    label: {
        fontFamily: theme.typography.medium,
        color: theme.palette.secondary.contrastText,
        fontSize: 15,
        marginBottom: 5,
    },
    labelError: {
        color: 'red',
    },
    helperText: {
        fontFamily: theme.typography.light,
        color: theme.palette.secondary.contrastText,
    },
    helperTextError: {
        fontFamily: theme.typography.light,
        color: 'red',
    },
}));

type Props = {
    name: string;
    label?: string;
    placeholder?: string;
    helperText?: string;
    value?: string;
    onChange?: string;
    variant?: 'filled' | 'outlined' | 'standard';
    required?: boolean;
    disabled?: boolean;
    rules?: {
        required?: boolean;
    };
    InputProps?: any;
    defaultValue?: string | null | undefined;
};
